import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { Formik } from 'formik';
import { Col, Form, Row, Spinner } from 'react-bootstrap';
import * as yup from 'yup';
import { AppContext } from '../../App';
import {
  createSupplierCredit,
  updateSupplierCredit,
} from '../../clients/supplier_credit_client';
import { getOrdersListForSupplier } from '../../clients/order_client';
import { showToast } from '../../config_and_helpers/helpers';
import { CURRENCY_SYMBOLS } from '../../config_and_helpers/config';
import { logInfo, logError } from '../../config_and_helpers/logger';
import NumberInput from '../Generic/NumberInput';
import { parseNumericInput, yupNumericField } from '../../utils/numberInput';

const formSchema = yup.object().shape({
  supplierId: yup.string().required('Supplier is required'),
  orderId: yup.string().nullable(),
  initialAmount: yupNumericField({
    required: true,
    min: 0,
    typeError: 'Enter a valid number',
  }),
  currentAmount: yupNumericField({
    required: false,
    min: 0,
    typeError: 'Enter a valid number',
  })
    .nullable()
    .transform((value, original) => (original === '' ? null : value))
    .test(
      'lte-initial',
      'Current amount cannot exceed initial amount',
      function (value) {
        if (value == null) return true;
        const { initialAmount } = this.parent;
        if (initialAmount == null) return true;
        return value <= initialAmount;
      }
    ),
  currency: yup.string().required('Currency is required'),
  name: yup.string(),
});

/**
 * Modal for creating or editing a supplier credit.
 *
 * @param {boolean} show - Whether the modal is visible.
 * @param {function} onHide - Called when the modal is closed.
 * @param {function} onSuccess - Called after a successful create/update (refetch list, etc.).
 * @param {Array} supplierSelectList - Array of { value, label } for supplier dropdown (used in create mode).
 * @param {string|number} preselectedSupplierId - Optional supplier id to prefill (create mode).
 * @param {Object} creditObj - When provided the modal operates in edit mode, pre-populating from this object.
 */
const SupplierCreditCreateModal = ({
  show,
  onHide,
  onSuccess,
  supplierSelectList = [],
  preselectedSupplierId = '',
  creditObj = null,
}) => {
  const { token } = useContext(AppContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderList, setOrderList] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [amountGuardMessage, setAmountGuardMessage] = useState('');

  const isEditMode = !!creditObj;

  const fetchOrdersForSupplier = useCallback(
    supplierId => {
      if (!supplierId) {
        setOrderList([]);
        return;
      }
      setOrdersLoading(true);
      getOrdersListForSupplier(token, supplierId).then(data => {
        setOrderList(Array.isArray(data) ? data : []);
        setOrdersLoading(false);
      });
    },
    [token]
  );

  const editSupplierId = useMemo(() => {
    if (!creditObj) return '';
    if (typeof creditObj.supplier === 'object' && creditObj.supplier)
      return String(creditObj.supplier.id ?? creditObj.supplier_id ?? '');
    return String(creditObj.supplier_id ?? creditObj.supplier ?? '');
  }, [creditObj]);

  const editOrderId = useMemo(() => {
    if (!creditObj) return '';
    if (creditObj.originating_order)
      return String(creditObj.originating_order.id ?? '');
    if (creditObj.order != null) {
      return String(
        typeof creditObj.order === 'object' ? creditObj.order.id : creditObj.order
      );
    }
    return '';
  }, [creditObj]);

  useEffect(() => {
    const suppId = isEditMode ? editSupplierId : preselectedSupplierId;
    if (show && suppId) {
      fetchOrdersForSupplier(suppId);
    } else if (!show) {
      setOrderList([]);
    }
  }, [show, preselectedSupplierId, editSupplierId, isEditMode, fetchOrdersForSupplier]);

  const initialValues = useMemo(() => {
    if (isEditMode) {
      return {
        supplierId: editSupplierId,
        orderId: editOrderId,
        initialAmount: creditObj.initial_amount ?? '',
        currentAmount: creditObj.remaining_amount ?? '',
        currency: creditObj.currency || 'NIS',
        name: creditObj.name || '',
      };
    }
    return {
      supplierId: preselectedSupplierId || '',
      orderId: '',
      initialAmount: '',
      currentAmount: '',
      currency: 'NIS',
      name: '',
    };
  }, [isEditMode, creditObj, editSupplierId, editOrderId, preselectedSupplierId]);

  const currencyOptions = Object.keys(CURRENCY_SYMBOLS).map(code => ({
    value: code,
    label: `${code} (${CURRENCY_SYMBOLS[code]})`,
  }));

  const handleSubmit = values => {
    setIsSubmitting(true);

    if (isEditMode) {
      const payload = {
        initial_amount: parseNumericInput(values.initialAmount) ?? 0,
        remaining_amount:
          values.currentAmount !== '' && values.currentAmount != null
            ? parseNumericInput(values.currentAmount)
            : null,
        currency: values.currency,
        name: values.name?.trim() || null,
        order: values.orderId ? Number(values.orderId) : null,
      };
      logInfo('SupplierCreditCreateModal', 'submitting credit update', {
        creditId: creditObj.id,
        ...payload,
      });
      updateSupplierCredit(token, creditObj.id, payload).then(response => {
        if (response && response.success) {
          logInfo('SupplierCreditCreateModal', 'credit updated', { id: creditObj.id });
          showToast('Supplier credit updated successfully.', 'success', 'top-right', 3000);
          onSuccess?.();
          onHide?.();
        } else {
          const message = Array.isArray(response) ? response.join(', ') : 'Failed to update credit.';
          logError('SupplierCreditCreateModal', 'credit update failed', { errors: response });
          showToast(message, 'error', 'top-right', 4000);
        }
        setIsSubmitting(false);
      });
    } else {
      const payload = {
        supplier: Number(values.supplierId),
        ...(values.orderId ? { order: Number(values.orderId) } : {}),
        initial_amount: parseNumericInput(values.initialAmount) ?? 0,
        ...(values.currentAmount !== '' && values.currentAmount != null
          ? {
              remaining_amount: parseNumericInput(values.currentAmount),
            }
          : {}),
        currency: values.currency,
        name: values.name?.trim() || null,
      };
      logInfo('SupplierCreditCreateModal', 'submitting credit creation', {
        supplier: payload.supplier,
        initial_amount: payload.initial_amount,
        remaining_amount: payload.remaining_amount ?? null,
        currency: payload.currency,
      });
      createSupplierCredit(token, payload).then(response => {
        if (response && response.success) {
          logInfo('SupplierCreditCreateModal', 'credit created', { id: response.data?.id });
          showToast('Supplier credit created successfully.', 'success', 'top-right', 3000);
          onSuccess?.();
          onHide?.();
        } else {
          const message = Array.isArray(response) ? response.join(', ') : 'Failed to create credit.';
          logError('SupplierCreditCreateModal', 'credit creation failed', { errors: response });
          showToast(message, 'error', 'top-right', 4000);
        }
        setIsSubmitting(false);
      });
    }
  };

  return (
    <Modal show={show} onHide={onHide} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditMode
            ? `Edit supplier credit ${
                creditObj.name || `#${creditObj.id}`
              }`
            : 'Create supplier credit'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik
          initialValues={initialValues}
          validationSchema={formSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            handleSubmit,
            handleChange,
            handleBlur,
            values,
            errors,
            touched,
            setFieldValue,
            isValid,
          }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={4}>
                  Supplier <span className="text-danger">*</span>
                </Form.Label>
                <Col sm={8}>
                  {isEditMode ? (
                    <Form.Control
                      plaintext
                      readOnly
                      value={
                        creditObj.supplier_name ||
                        (typeof creditObj.supplier === 'object'
                          ? creditObj.supplier.name
                          : '') ||
                        `Supplier #${editSupplierId}`
                      }
                    />
                  ) : (
                    <>
                      <Form.Select
                        name="supplierId"
                        value={values.supplierId}
                        onChange={e => {
                          handleChange(e);
                          setFieldValue('orderId', '');
                          fetchOrdersForSupplier(e.target.value);
                        }}
                        onBlur={handleBlur}
                        isInvalid={touched.supplierId && errors.supplierId}
                      >
                        <option value="">-- Select supplier --</option>
                        {supplierSelectList.map((opt, idx) => (
                          <option key={idx} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.supplierId}
                      </Form.Control.Feedback>
                    </>
                  )}
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={4}>
                  Related order
                </Form.Label>
                <Col sm={8}>
                  {ordersLoading ? (
                    <div className="d-flex align-items-center">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <span className="text-muted">Loading orders...</span>
                    </div>
                  ) : (
                    <Form.Select
                      name="orderId"
                      value={values.orderId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.orderId && errors.orderId}
                      disabled={!isEditMode && !values.supplierId}
                    >
                      <option value="">
                        {values.supplierId || isEditMode
                          ? '-- Select order --'
                          : 'Select a supplier first'}
                      </option>
                      {orderList.map(order => (
                        <option key={order.id} value={order.id}>
                          {order.corporate_order_ref || `Order #${order.id}`}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                  <Form.Control.Feedback type="invalid">
                    {errors.orderId}
                  </Form.Control.Feedback>
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={4}>
                  Initial amount <span className="text-danger">*</span>
                </Form.Label>
                <Col sm={8}>
                  <NumberInput
                    name="initialAmount"
                    value={values.initialAmount}
                    onChange={event => {
                      handleChange(event);
                      const nextInitialRaw = event.target.value;
                      const nextInitial = parseNumericInput(nextInitialRaw);
                      const current = parseNumericInput(values.currentAmount);
                      const hasCurrent =
                        values.currentAmount !== '' && values.currentAmount != null;
                      if (
                        nextInitialRaw !== '' &&
                        nextInitial != null &&
                        hasCurrent &&
                        current != null &&
                        current > nextInitial
                      ) {
                        setFieldValue('currentAmount', String(nextInitial), false);
                        setAmountGuardMessage(
                          'Current amount cannot exceed initial amount.'
                        );
                      } else if (amountGuardMessage) {
                        setAmountGuardMessage('');
                      }
                    }}
                    onBlur={handleBlur}
                    isInvalid={touched.initialAmount && errors.initialAmount}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.initialAmount}
                  </Form.Control.Feedback>
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={4}>
                  Current amount
                </Form.Label>
                <Col sm={8}>
                  <NumberInput
                    name="currentAmount"
                    value={values.currentAmount}
                    onChange={event => {
                      const nextCurrentRaw = event.target.value;
                      const initial = parseNumericInput(values.initialAmount);
                      const nextCurrent = parseNumericInput(nextCurrentRaw);
                      const hasInitial =
                        values.initialAmount !== '' && values.initialAmount != null;
                      if (
                        hasInitial &&
                        nextCurrentRaw !== '' &&
                        initial != null &&
                        nextCurrent != null &&
                        nextCurrent > initial
                      ) {
                        setFieldValue('currentAmount', String(initial), false);
                        setAmountGuardMessage(
                          'Current amount cannot exceed initial amount.'
                        );
                        return;
                      }
                      setAmountGuardMessage('');
                      handleChange(event);
                    }}
                    onBlur={handleBlur}
                    isInvalid={touched.currentAmount && errors.currentAmount}
                    placeholder="Defaults to initial amount"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.currentAmount}
                  </Form.Control.Feedback>
                  {amountGuardMessage && (
                    <Form.Text className="text-muted">{amountGuardMessage}</Form.Text>
                  )}
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={4}>
                  Currency <span className="text-danger">*</span>
                </Form.Label>
                <Col sm={8}>
                  <Form.Select
                    name="currency"
                    value={values.currency}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.currency && errors.currency}
                  >
                    {currencyOptions.map((opt, idx) => (
                      <option key={idx} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.currency}
                  </Form.Control.Feedback>
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={4}>
                  Name (optional)
                </Form.Label>
                <Col sm={8}>
                  <Form.Control
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Display name for this credit"
                  />
                </Col>
              </Form.Group>

              <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting || !isValid}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {isEditMode ? 'Saving...' : 'Creating...'}
                    </>
                  ) : isEditMode ? (
                    'Save changes'
                  ) : (
                    'Create credit'
                  )}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default SupplierCreditCreateModal;
